import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Quantity stepper for the sticky add-to-cart bar.
 *
 * It owns no quantity state: plus/minus proxy to the buy-buttons block's
 * quantity-selector-component in the same section, so the main form input
 * (the one the add-to-cart submit reads) stays the single source of truth.
 * The displayed number follows ThemeEvents.quantitySelectorUpdate, which the
 * main selector dispatches on every change.
 *
 * @typedef {object} StickyQuantityRefs
 * @property {HTMLElement} quantityValue - The displayed quantity number.
 *
 * @extends {Component<StickyQuantityRefs>}
 */
class StickyQuantityComponent extends Component {
  requiredRefs = ['quantityValue'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.#syncFromMain();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /** @returns {import('./component-quantity-selector').QuantitySelectorComponent | null} */
  get #mainSelector() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('.buy-buttons-block quantity-selector-component') ?? null;
  }

  increase = (event) => {
    this.#mainSelector?.increaseQuantity(event);
    this.#syncFromMain();
  };

  decrease = (event) => {
    this.#mainSelector?.decreaseQuantity(event);
    this.#syncFromMain();
  };

  #handleQuantityUpdate = (event) => {
    // Ignore cart-line quantity events (they carry a cartLine id)
    if (event.detail?.cartLine) return;
    if (typeof event.detail?.quantity === 'number') {
      this.refs.quantityValue.textContent = String(event.detail.quantity);
    }
  };

  #syncFromMain = () => {
    const input = this.#mainSelector?.querySelector('input[name="quantity"]');
    if (input) this.refs.quantityValue.textContent = input.value || '1';
  };
}

if (!customElements.get('sticky-quantity-component')) {
  customElements.define('sticky-quantity-component', StickyQuantityComponent);
}
